import { InvalidFormField, type Scan, type ScanReviewGroup } from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { addScanToCart, loadCarts, removeLineFromCart } from "../../core/ops/cart-ops";
import {
  createPosCustomer,
  loadCustomerReviewQueue,
  loadCustomerScans,
  loadCustomers,
} from "../../core/ops/customer-ops";
import { confirmGroup, pickGroupCandidate, rejectGroup } from "../../core/ops/review-ops";
import { deleteScan } from "../../core/ops/scan-ops";
import { cartLineForScan, openCartTotals, quoteNote } from "../../core/primitives/customer-cart";
import { CUSTOMER_ADD_FAILED_ON_THE_WAY, customerLabel } from "../../core/primitives/customer-label";
import { errorText } from "../../core/primitives/errors";
import { failedLoad, loaded, loading } from "../../core/primitives/load-status";
import {
  customerDrafts,
  customerReviewGroups,
  customerRowKey,
  READ_FAILED_ON_THE_WAY,
  type ReviewDraft,
  reviewEntry,
  withoutDrafts,
} from "../../core/primitives/review-row";
import { shopEntryId } from "../../core/primitives/workspace";
import { type Capture, type ScanSession, scanIdOf, withKept } from "../../core/services/scan-session";
import { CustomerCartController } from "../../sections/customer-cart/customer-cart-controller";
import { CustomerScansActions, CustomerScansState } from "./customer-scans-model";

export type CustomerScansDeps = Pick<
  AppDeps,
  | "client"
  | "authHandler"
  | "storeRepository"
  | "providersRepository"
  | "customersRepository"
  | "pendingCartsRepository"
  | "reviewQueueRepository"
  | "stripeTerminal"
  | "scanSessions"
>;

const overlaps = (a: readonly string[], b: readonly string[]) => a.some((id) => b.includes(id));

/** A thrown call, kept to be handled like an answer, then thrown on. */
const kept = (thrown: unknown) => ({ thrown });

export interface CustomerScansParams {
  /** `?customer=<id>`; null shows the list. */
  readonly customerId: string | null;
}

export interface CustomerScansRouting {
  showCustomer(id: string): void;
  showAllCustomers(): void;
  /** The list, in place of a customer the page cannot show: the counter has not listed them since it was chosen. */
  leaveCustomer(): void;
  editScan(scanId: string): void;
  /** The card page, with what the customer was quoted over it when the page knows who they are. */
  openCard(cardId: string, quoteNote: string | null): void;
  /** The scanner, scanning on this counter customer's behalf. */
  scanFor(customerId: string): void;
}

/** The customer list, or one customer's scans. One controller per selection (the route keys it by `customerId`). */
export class CustomerScansController extends SubtreeController implements CustomerScansActions {
  readonly state: CustomerScansState;
  /** What Checkout does to the cart, as on the Carts tab. */
  readonly cart: CustomerCartController;
  readonly now: () => Date;
  #requested = false;
  /** Reads of the scans are numbered when asked; only the newest one asked writes. */
  #scansAsked = 0;
  /** The newest read of the scans, which an older one settles with. */
  #scansRead: Promise<void> = Promise.resolve();
  /** Reads of the cart, the same way: an older answer would bring back a cart a payment or a delete closed. */
  #cartAsked = 0;
  #cartRead: Promise<void> = Promise.resolve();
  /**
   * The scans the confirm or reject on its way covers, and those the switch on
   * its way covers. The guards go by these, not by row keys: a read can move a
   * row's key mid-action (its oldest scan reviewed elsewhere, a refused queue
   * read standing its scans alone).
   */
  #reviewing: readonly string[] = [];
  #switching: readonly string[] = [];
  #session: ScanSession | null = null;
  /** The session's cards seen landed, and those still listed, as last seen, until a read of the scans lands with them. */
  readonly #seenLanded = new Set<string>();
  readonly #landed = new Map<string, Capture>();
  /** Scans the page has listed: their cards get no row of their own again, even once a reject takes them off the list. */
  readonly #listedScans = new Set<string>();
  /** Confirms and rejects that threw, whether they went through left to the next read that answers. */
  #undecided: { typed: ReviewDraft; sharing: readonly string[]; scanIds: readonly string[] }[] = [];

  constructor(
    readonly params: CustomerScansParams,
    readonly deps: CustomerScansDeps,
    readonly routing: CustomerScansRouting,
    { now = () => new Date() }: { now?: () => Date } = {},
  ) {
    super();
    this.now = now;
    this.state = new CustomerScansState(params.customerId);
    this.subtree.put(CustomerScansState, this.state);
    this.subtree.put(CustomerScansActions, this);
    // What Checkout changes moves the scans' "In cart" too, so both are read again.
    this.cart = this.own(new CustomerCartController(this.subtree, deps, { reload: () => this.#readCartAgain() }));

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      this.state.storeId.value = shopId;
      if (shopId === null) return;
      const repo = deps.customersRepository;
      if (repo.status.kind === "idle" || repo.isStale)
        void loadCustomers({ ...deps, shopId, locationId: deps.storeRepository.locationId });
      if (!this.#requested) {
        this.#requested = true;
        if (params.customerId === null) {
          void this.#loadCartTotals(shopId);
        } else {
          void this.#loadScans(shopId, params.customerId);
          void this.#loadCart(shopId, params.customerId);
        }
      }
    }, [deps.storeRepository]);

    this.sync(() => {
      const { shopId, locationId } = deps.storeRepository;
      this.state.openCarts.value =
        shopId === null ? 0 : deps.pendingCartsRepository.countFor(shopEntryId(shopId, locationId));
    }, [deps.pendingCartsRepository, deps.storeRepository]);

    this.sync(() => {
      const repo = deps.customersRepository;
      this.state.customers.value = repo.customers;
      this.state.customersStatus.value = repo.status;
      const id = params.customerId;
      this.state.selectedCustomer.value = id === null ? null : (repo.named(id) ?? null);
      // Another counter's customer, reached by Back or a review's way back after a counter change: nameless here.
      const { shopId, locationId } = deps.storeRepository;
      if (id === null || this.state.selectedCustomer.value !== null || shopId === null) return;
      if (repo.status.kind !== "loaded" || repo.entry !== shopEntryId(shopId, locationId)) return;
      // A list marked stale meanwhile is read again first: nothing else on this page would ask.
      if (repo.isStale) void loadCustomers({ ...deps, shopId, locationId }).catch(() => undefined);
      else this.routing.leaveCustomer();
    }, [deps.customersRepository]);

    this.sync(() => this.#follow(), [deps.storeRepository]);
    // A card the page lists has no row of its own on its way.
    this.sync(() => this.#mirror(), [this.state.scans, this.state.reviewGroups]);
    this.autoDispose(() => this.#session?.removeListener(this.#mirror));
  }

  selectCustomer = (id: string) => {
    this.routing.showCustomer(id);
  };

  showAllCustomers = () => {
    this.routing.showAllCustomers();
  };

  editScan = (scan: Scan) => {
    this.routing.editScan(scan.id);
  };

  openScan = (scan: Scan) => {
    if (scan.cardId === null) return this.routing.editScan(scan.id);
    // The note names the customer, so it waits for the list that does; the card opens either way.
    const customer = this.state.selectedCustomer.value;
    const inCart = cartLineForScan(this.state.cart.value ? [this.state.cart.value] : [], scan) !== null;
    this.routing.openCard(scan.cardId, customer && quoteNote(customerLabel(customer), scan, inCart));
  };

  askDelete = (scan: Scan) => {
    if (this.state.busyScanId.value === null) this.state.deleting.value = scan;
  };

  cancelDelete = () => {
    if (this.state.busyScanId.value === null) this.state.deleting.value = null;
  };

  deleteScan = async () => {
    const scan = this.state.deleting.value;
    const shopId = this.deps.storeRepository.shopId;
    if (scan === null || shopId === null || this.state.busyScanId.value !== null) return;
    try {
      await this.#cartAction(scan.id, () => deleteScan({ ...this.deps, shopId, scanId: scan.id }));
    } finally {
      if (!this.isDisposed) this.state.deleting.value = null;
    }
  };

  setPrice = (group: ScanReviewGroup, price: string) => {
    const key = customerRowKey(group);
    this.state.drafts.update((d) => ({ ...d, [key]: { ...d[key], price } }));
  };

  confirm = (group: ScanReviewGroup) => {
    const entry = reviewEntry(group, this.state.drafts.value[customerRowKey(group)]);
    if (!entry) return Promise.resolve();
    return this.#review(group, "Confirm failed.", (shopId) => confirmGroup({ ...this.deps, shopId, entry }));
  };

  reject = (group: ScanReviewGroup) =>
    this.#review(group, "Reject failed.", (shopId) => rejectGroup({ ...this.deps, shopId, group }));

  pick = async (group: ScanReviewGroup, productId: string) => {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    const key = customerRowKey(group);
    if (shopId === null || customerId === null || this.state.picking.value !== null) return;
    if (!this.#listed(group) || overlaps(group.scanIds, this.#reviewing)) return;
    this.state.picking.value = { key, productId };
    this.#switching = group.scanIds;
    this.#say(null, null);
    try {
      const r = await pickGroupCandidate({ ...this.deps, shopId, group, productId }).catch(kept);
      if ("thrown" in r) {
        await this.#thrownOn(shopId, customerId, "Could not switch the match.", key);
        throw r.thrown;
      }
      if (this.isDisposed) return;
      if (!r.ok) this.#say(errorText(r.errors) || "Could not switch the match.", key);
      // The pick moves the estimate, the comps and possibly the match.
      await this.#loadScans(shopId, customerId);
    } finally {
      this.#switching = [];
      if (!this.isDisposed) this.state.picking.value = null;
    }
  };

  retry = () => {
    const { shopId, locationId } = this.deps.storeRepository;
    if (shopId === null) return;
    if (this.state.customersStatus.value.kind === "error") void loadCustomers({ ...this.deps, shopId, locationId });
    if (this.params.customerId !== null) {
      void this.#loadScans(shopId, this.params.customerId);
      if (this.state.cartStatus.value.kind === "error") void this.#loadCart(shopId, this.params.customerId);
    }
  };

  addToCart = async (scan: Scan) => {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    if (shopId === null || customerId === null || this.state.busyScanId.value !== null) return;
    if (this.state.cartStatus.value.kind !== "loaded") return;
    await this.#cartAction(scan.id, async () =>
      addScanToCart({
        ...this.deps,
        shopId,
        customerId,
        scanId: scan.id,
        openCartId: this.state.cart.value?.id ?? null,
      }),
    );
  };

  removeFromCart = async (scan: Scan) => {
    // The cart as last read decides between removing a line and deleting the cart, so it must be read.
    if (this.state.cartStatus.value.kind !== "loaded") return;
    const line = cartLineForScan(this.state.cart.value ? [this.state.cart.value] : [], scan);
    if (!line || this.state.busyScanId.value !== null) return;
    await this.#cartAction(scan.id, () => removeLineFromCart({ ...this.deps, cart: line.cart, itemId: line.item.id }));
  };

  checkout = () => {
    // Not while an add or remove is on its way: Checkout would act on the cart before that change.
    const cart = this.state.cart.value;
    if (cart && this.state.busyScanId.value === null) this.state.checkout.value = { cartId: cart.id, cart };
  };

  closeCheckout = () => {
    // The card reader opens over Checkout and closes on its own; a change in it is read back first.
    if (this.cart.busy()) return;
    this.state.checkout.value = null;
    this.cart.reset();
  };

  scanForCustomer = () => {
    if (this.params.customerId !== null) this.routing.scanFor(this.params.customerId);
  };

  retryCapture = (id: string) => {
    this.#session?.retry(id);
  };

  discardCapture = (id: string) => {
    this.#session?.discard(id);
  };

  /** The customer's session at the counter chosen: what this browser scans for them lands here. */
  #follow(): void {
    if (this.isDisposed) return;
    const { storeRepository, scanSessions } = this.deps;
    const shopId = storeRepository.shopId;
    const customerId = this.params.customerId;
    const session =
      shopId === null || customerId === null
        ? null
        : scanSessions.for({ shopId, locationId: storeRepository.locationId, customerId });
    if (session === this.#session) return;
    this.#session?.removeListener(this.#mirror);
    this.#session = session;
    this.#seenLanded.clear();
    this.#landed.clear();
    this.#listedScans.clear();
    session?.addListener(this.#mirror);
    this.#mirror();
  }

  /**
   * The session's cards on their way, newest first, above the customer's scans, as iOS's. A card that lands is read
   * with the customer's scans and keeps its row until that read lands, so it does not blink out of the page.
   */
  #mirror = () => {
    const session = this.#session;
    if (session === null) {
      this.state.captures.value = [];
      return;
    }
    const listed = new Set([
      ...this.state.reviewGroups.value.flatMap((g) => g.scanIds),
      ...this.state.scans.value
        .filter((s) => s.review === "CONFIRMED" || s.recognitionOutcome === "no_match")
        .map((s) => s.id),
    ]);
    for (const id of listed) this.#listedScans.add(id);
    // A card whose fate was unknown and whose scan the page lists is read again: the session's notice runs this again.
    let asked = false;
    for (const capture of session.captures) {
      if (capture.unsure && listed.has(scanIdOf(capture) ?? "")) asked = session.listed(capture.id) || asked;
    }
    if (asked) return;
    let landed = false;
    for (const capture of session.captures) {
      if (capture.status !== "ready") continue;
      if (this.#landed.has(capture.id)) this.#landed.set(capture.id, capture);
      if (this.#seenLanded.has(capture.id)) continue;
      this.#seenLanded.add(capture.id);
      this.#landed.set(capture.id, capture);
      landed = true;
    }
    // One row per card: none of its own once the page has listed its scan, even after a reject takes it off the list.
    // A landed card's row outlives the session letting it go, until a read of the scans lands with it. A card that
    // failed keeps its row, as on Review: its listed scan says nothing of the failure, and Retry is on the row.
    this.state.captures.value = withKept(session.captures, this.#landed).filter((c) => {
      const scanId = scanIdOf(c);
      if (c.status !== "failed" && scanId !== null && this.#listedScans.has(scanId)) return false;
      return c.status !== "ready" || this.#landed.has(c.id);
    });
    const { shopId, customerId } = session.scope;
    if (landed && customerId !== null) void this.#loadScans(shopId, customerId).catch(() => undefined);
  };

  /**
   * A read of the scans landed: every card that landed is in it, since each landing asks a read, and only the newest
   * read asked lands.
   */
  #settleLanded(): void {
    if (this.#landed.size === 0) return;
    this.#landed.clear();
    this.#mirror();
  }

  dismissActionError = () => {
    this.#say(null, null);
  };

  openNewCustomer = () => {
    this.state.newCustomer.value = { name: "", busy: false, error: null, failure: null };
  };

  setNewCustomerName = (name: string) => {
    this.state.newCustomer.update((d) => (d ? { ...d, name, error: null } : d));
  };

  closeNewCustomer = () => {
    // Not while the customer is being made: the answer opens their page.
    this.state.newCustomer.update((d) => (d?.busy ? d : null));
  };

  createCustomer = async () => {
    const draft = this.state.newCustomer.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!draft || draft.busy || shopId === null) return;
    if (!draft.name.trim()) {
      this.state.newCustomer.value = { ...draft, error: "Give the customer a name." };
      return;
    }
    this.state.newCustomer.value = { ...draft, busy: true, error: null, failure: null };
    const locationId = this.deps.storeRepository.locationId;
    const r = await createPosCustomer({ ...this.deps, shopId, locationId, name: draft.name }).catch(
      async (thrown: unknown) => {
        if (!this.isDisposed) {
          // It may have gone through: the dialog is free again, with a sentence, and the list is read again, by a
          // read asked now, not one already on its way that could not have seen it.
          this.#newCustomerAnswered({ error: null, failure: CUSTOMER_ADD_FAILED_ON_THE_WAY });
          await loadCustomers({ ...this.deps, shopId, locationId, fresh: true }).catch(() => undefined);
        }
        throw thrown;
      },
    );
    if (this.isDisposed) return;
    if (!r.ok) {
      // A refusal of the name is the name's; anything else (Point of Sale switched off) is the dialog's.
      const said = errorText(r.errors);
      const onName = r.errors.some((e) => e instanceof InvalidFormField && e.fieldName === "name");
      this.#newCustomerAnswered(onName ? { error: said, failure: null } : { error: null, failure: said });
      return;
    }
    this.state.newCustomer.value = null;
    this.routing.showCustomer(r.value.id);
  };

  /** The create answered: the dialog is free again, keeping the name as it now reads. */
  #newCustomerAnswered(said: { error: string | null; failure: string | null }): void {
    this.state.newCustomer.update((d) => (d ? { ...d, busy: false, ...said } : d));
  }

  /**
   * The customer's scans with the queue's groups for them. Resolves once the list
   * on screen is the newest one asked for: a read a newer one overtook settles
   * with that one, so whoever waits on it never sees the list before its change.
   */
  #loadScans(shopId: string, customerId: string): Promise<void> {
    const read = this.#readScans(shopId, customerId, ++this.#scansAsked);
    this.#scansRead = read;
    return read;
  }

  /**
   * A read after the first keeps the list on screen. A failed queue read costs the
   * rows their grouping and differences, not the list, and the page says so; one
   * that throws is shown the same way, then thrown on, as unexpected. A scans
   * read that throws is shown as a refused one is, then thrown on; one a newer
   * read overtook settles with that one either way.
   */
  async #readScans(shopId: string, customerId: string, asked: number): Promise<void> {
    if (this.state.scansStatus.value.kind !== "loaded") this.state.scansStatus.value = loading;
    const locationId = this.deps.storeRepository.locationId;
    const [r, queue] = await Promise.all([
      loadCustomerScans({ ...this.deps, shopId, locationId, customerId }).catch(kept),
      loadCustomerReviewQueue({ ...this.deps, shopId, locationId }).catch(kept),
    ]);
    if (this.isDisposed) return;
    if (asked !== this.#scansAsked) return this.#scansRead;
    if ("thrown" in r) {
      this.state.scansStatus.value = { kind: "error", message: READ_FAILED_ON_THE_WAY };
      throw r.thrown;
    }
    if (!r.ok) {
      this.state.scansStatus.value = failedLoad(r.errors);
    } else {
      const answered = "thrown" in queue || !queue.ok ? null : queue.value;
      const groups = customerReviewGroups(r.value, answered ?? [], this.now());
      const before = this.state.reviewGroups.value;
      this.state.scans.value = r.value;
      this.state.reviewGroups.value = groups;
      this.state.queueFailed.value = answered === null;
      this.state.drafts.update((d) => customerDrafts(d, before, groups));
      this.state.scansStatus.value = loaded;
      this.#decideThrown();
      this.#settleLanded();
    }
    if ("thrown" in queue) throw queue.thrown;
  }

  /**
   * One confirm or reject at a time, and none for a row whose match is being
   * switched. The scans are read again after it, whatever it answered, and a
   * confirmed row takes its draft with it.
   */
  async #review(
    group: ScanReviewGroup,
    fallback: string,
    op: (shopId: string) => Promise<Result<void, Error>>,
  ): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    const key = customerRowKey(group);
    if (shopId === null || customerId === null || this.state.busyKey.value !== null) return;
    if (!this.#listed(group) || overlaps(group.scanIds, this.#switching)) return;
    this.state.busyKey.value = key;
    this.#reviewing = group.scanIds;
    this.#say(null, null);
    const drafts = this.state.drafts.value;
    const typed = drafts[key];
    const sharing = this.state.reviewGroups.value
      .filter((row) => drafts[customerRowKey(row)] === typed)
      .flatMap((row) => row.scanIds)
      .filter((id) => !group.scanIds.includes(id));
    try {
      const r = await op(shopId).catch(kept);
      if ("thrown" in r) {
        await this.#thrownOn(shopId, customerId, fallback, key);
        if (!this.isDisposed && typed !== undefined) {
          this.#undecided.push({ typed, sharing, scanIds: group.scanIds });
          this.#decideThrown();
        }
        throw r.thrown;
      }
      if (this.isDisposed) return;
      if (!r.ok) this.#say(errorText(r.errors) || fallback, key);
      else this.#dropDraft(typed, sharing);
      await this.#loadScans(shopId, customerId);
    } finally {
      this.#reviewing = [];
      if (!this.isDisposed) this.state.busyKey.value = null;
    }
  }

  /**
   * A row a confirm or reject settled takes its draft with it, from every row a
   * read carried it to, so no newer copy of the card inherits it. The rows that
   * showed it too when the action began (a refused queue read stands a card's
   * scans apart, each with the card's draft) keep it while they hold one of
   * their scans. The row was busy meanwhile, so nothing typed a new one.
   */
  #dropDraft(typed: ReviewDraft | undefined, sharing: readonly string[]): void {
    if (typed === undefined) return;
    const keep = new Set(
      this.state.reviewGroups.value.filter((row) => overlaps(row.scanIds, sharing)).map(customerRowKey),
    );
    this.state.drafts.update((d) =>
      withoutDrafts(
        d,
        Object.keys(d).filter((k) => d[k] === typed && !keep.has(k)),
      ),
    );
  }

  /**
   * Whether the row is the one on the page now. A click drawn before a read
   * redrew the rows would act on scans and a price the page no longer shows (a
   * key the read moved, or one that now holds another row's draft), so it does
   * nothing; the redrawn row is a click away.
   */
  #listed(group: ScanReviewGroup): boolean {
    return this.state.reviewGroups.value.includes(group);
  }

  /**
   * Once a read of the scans has answered: a confirm or reject that threw, none
   * of whose scans is pending, went through after all and takes its draft.
   */
  #decideThrown(): void {
    if (this.state.scansStatus.value.kind !== "loaded") return;
    const pending = new Set(this.state.scans.value.filter((s) => s.review === "PENDING").map((s) => s.id));
    const undecided = this.#undecided;
    this.#undecided = [];
    for (const call of undecided) {
      if (!call.scanIds.some((id) => pending.has(id))) this.#dropDraft(call.typed, call.sharing);
    }
  }

  /** What a row's action answered, said under that row while it is listed. */
  #say(text: string | null, at: string | null): void {
    this.state.actionError.value = text;
    this.state.actionErrorAt.value = at;
  }

  /**
   * After a call that threw: it may have gone through, so the scans are read
   * again under its sentence before the throw goes on. A failure of that read is
   * shown on the page, and the call's own throw is the one reported.
   */
  async #thrownOn(shopId: string, customerId: string, sentence: string, at: string | null): Promise<void> {
    if (this.isDisposed) return;
    this.#say(sentence, at);
    await this.#loadScans(shopId, customerId).catch(() => undefined);
  }

  /** The customer's open cart. Resolves once the cart on screen is the newest one asked for, as the scans do. */
  #loadCart(shopId: string, customerId: string): Promise<void> {
    const read = this.#readCart(shopId, customerId, ++this.#cartAsked);
    this.#cartRead = read;
    return read;
  }

  /** A read that throws is shown as a refused one is, then thrown on. */
  async #readCart(shopId: string, customerId: string, asked: number): Promise<void> {
    this.state.cartStatus.value = loading;
    const r = await loadCarts({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId }).catch(kept);
    if (this.isDisposed) return;
    if (asked !== this.#cartAsked) return this.#cartRead;
    if ("thrown" in r) {
      this.state.cartStatus.value = { kind: "error", message: READ_FAILED_ON_THE_WAY };
      throw r.thrown;
    }
    if (!r.ok) {
      this.state.cartStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.cart.value = r.value.find((c) => c.status === "OPEN" && c.user.id === customerId) ?? null;
    this.state.cartStatus.value = loaded;
    this.cart.cartRead();
    // Checkout follows its cart by id, so a paid one shows as paid there.
    const open = this.state.checkout.value;
    if (open)
      this.state.checkout.value = { cartId: open.cartId, cart: r.value.find((c) => c.id === open.cartId) ?? null };
  }

  /** What a change in Checkout waits for: its cart, read again. The scans, whose states follow it, are read beside it. */
  #readCartAgain(): Promise<boolean> {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    if (shopId === null || customerId === null) return Promise.resolve(false);
    void this.#loadScans(shopId, customerId);
    return this.#loadCart(shopId, customerId).then(() => this.state.cartStatus.value.kind === "loaded");
  }

  /** The customer list's "Cart $X.XX". A failed read shows no badge, as on iOS: the Carts tab is the list of carts. */
  async #loadCartTotals(shopId: string): Promise<void> {
    const r = await loadCarts({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    if (!this.isDisposed && r.ok) this.state.cartTotals.value = openCartTotals(r.value);
  }

  async #cartAction(scanId: string, op: () => Promise<{ ok: boolean; errors?: readonly Error[] }>): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    if (shopId === null || customerId === null) return;
    this.state.busyScanId.value = scanId;
    this.#say(null, null);
    try {
      const r = await op().catch(async (thrown: unknown) => {
        // It may have gone through, so the cart is read again with the scans.
        await Promise.all([
          this.#thrownOn(shopId, customerId, READ_FAILED_ON_THE_WAY, scanId),
          this.#loadCart(shopId, customerId).catch(() => undefined),
        ]);
        throw thrown;
      });
      if (this.isDisposed) return;
      if (!r.ok) this.#say(errorText(r.errors ?? []), scanId);
      // The server decides what the add or remove did to the cart, so both are read again.
      await Promise.all([this.#loadCart(shopId, customerId), this.#loadScans(shopId, customerId)]);
    } finally {
      if (!this.isDisposed) this.state.busyScanId.value = null;
    }
  }
}
